import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import { TicketTypeSummaryResult } from 'stores/TicketTypeStoreAdapter';

/** 種別のライフサイクル状態 */
export type TypeLifecycle = 'DRAFT' | 'ACTIVE' | 'INACTIVE';
export const lifecycleOf = (t: TicketTypeSummaryResult): TypeLifecycle => {
  if (t.isActive) return 'ACTIVE';
  return t.hasBeenActivated ? 'INACTIVE' : 'DRAFT';
};

const LIFECYCLE_META: Record<TypeLifecycle, { label: string; color: string; dot: string }> = {
  DRAFT: { label: '未有効化', color: 'text.secondary', dot: '#9e9e9e' },
  ACTIVE: { label: '有効中', color: 'success.main', dot: '#2e7d32' },
  INACTIVE: { label: '無効化', color: 'warning.main', dot: '#ed6c02' },
};

type Props = {
  /** 選択スコープの種別一覧 */
  types: TicketTypeSummaryResult[];
  selectedId: string | null;
  onSelect: (typeId: string) => void;
  /** 新規追加（＝そのスコープの設定を追加） */
  onAdd: () => void;
  /** 選択中種別のライフサイクル操作 */
  onActivate: (t: TicketTypeSummaryResult) => void;
  onDeactivate: (t: TicketTypeSummaryResult) => void;
  onDelete: (t: TicketTypeSummaryResult) => void;
  onRename: (t: TicketTypeSummaryResult) => void;
  /** 操作可否（グローバルのロール制約等） */
  canOperate: boolean;
  /** 追加ボタンのラベル（例: 「グローバルチケット設定の追加」） */
  addLabel: string;
};

/**
 * 種別セレクタ（設定画面 左ペイン上部）。
 * 申請フォーム設定にはない「種別」レイヤーを、2ペインのレイアウトを崩さず
 * 直感的に扱うためのUI。
 * - 大きな押下領域のドロップダウンで対象設定（種別）を選択（状態ドット＋分類チップ＋名称）
 * - 右に「追加」ボタン
 * - 選択中設定のライフサイクル操作（有効化/無効化/名称編集/削除）をインラインアクションで提供
 */
const TicketTypeSelector: React.FC<Props> = ({
  types,
  selectedId,
  onSelect,
  onAdd,
  onActivate,
  onDeactivate,
  onDelete,
  onRename,
  canOperate,
  addLabel,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const selected = types.find((t) => t.typeId === selectedId) ?? null;
  const selectedLc = selected ? lifecycleOf(selected) : null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block' }}
      >
        設定対象
      </Typography>
      <Stack direction="row" spacing={1} alignItems="stretch">
        {/* ドロップダウン本体（大きな押下領域） */}
        <Box ref={anchorRef} sx={{ flex: 1, minWidth: 0 }}>
          <Box
            onClick={() => types.length > 0 && setOpen((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: 1,
              borderColor: open ? 'primary.main' : 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              minHeight: 44,
              cursor: types.length > 0 ? 'pointer' : 'default',
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: types.length > 0 ? 'primary.main' : 'divider' },
            }}
          >
            {selected ? (
              <>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: LIFECYCLE_META[selectedLc as TypeLifecycle].dot,
                    flexShrink: 0,
                  }}
                />
                <TicketCategoryChip ticketCategory={selected.ticketCategory} />
                <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {selected.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: LIFECYCLE_META[selectedLc as TypeLifecycle].color, flexShrink: 0 }}
                >
                  {LIFECYCLE_META[selectedLc as TypeLifecycle].label}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {types.length === 0
                  ? '設定がありません。右の「追加」から作成してください'
                  : '設定を選択'}
              </Typography>
            )}
            {types.length > 0 && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: 'action.active',
                  transition: 'transform 0.2s',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
          >
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Paper
                elevation={0}
                sx={{
                  mt: 0.5,
                  maxHeight: 320,
                  overflow: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                {types.map((t) => {
                  const lc = lifecycleOf(t);
                  const isSel = t.typeId === selectedId;
                  return (
                    <Box
                      key={t.typeId}
                      onClick={() => {
                        onSelect(t.typeId);
                        setOpen(false);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: isSel ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: LIFECYCLE_META[lc].dot,
                          flexShrink: 0,
                        }}
                      />
                      <TicketCategoryChip ticketCategory={t.ticketCategory} />
                      <Typography
                        variant="body2"
                        fontWeight={isSel ? 700 : 400}
                        noWrap
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {t.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: LIFECYCLE_META[lc].color, flexShrink: 0 }}
                      >
                        {LIFECYCLE_META[lc].label}
                      </Typography>
                    </Box>
                  );
                })}
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>

        {/* 追加ボタン */}
        <Tooltip title={addLabel}>
          <span>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAdd}
              disabled={!canOperate}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minHeight: 44,
                borderStyle: 'dashed',
                '&:hover': { borderStyle: 'solid', boxShadow: 'none' },
              }}
            >
              追加
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* 選択中設定のライフサイクル操作（インライン） */}
      {selected && canOperate && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
        >
          {selectedLc === 'DRAFT' && (
            <>
              <LcButton
                icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label="有効化"
                onClick={() => onActivate(selected)}
              />
              <LcButton
                icon={<DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />}
                label="名称・設定を編集"
                onClick={() => onRename(selected)}
              />
              <LcButton
                icon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                label="削除"
                color="error"
                onClick={() => onDelete(selected)}
              />
            </>
          )}
          {selectedLc === 'ACTIVE' && (
            <>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  有効中は編集できません。編集するには無効化してください
                </Typography>
              </Stack>
              <LcButton
                icon={<PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label="無効化"
                color="warning"
                onClick={() => onDeactivate(selected)}
              />
            </>
          )}
          {selectedLc === 'INACTIVE' && (
            <>
              <LcButton
                icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label="再有効化"
                onClick={() => onActivate(selected)}
              />
              <LcButton
                icon={<DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />}
                label="名称編集"
                onClick={() => onRename(selected)}
              />
            </>
          )}
        </Stack>
      )}
      {selected && !canOperate && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            この設定を編集する権限がありません（参照のみ）
          </Typography>
        </Stack>
      )}
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
};

const LcButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color?: 'primary' | 'warning' | 'error';
}> = ({ icon, label, onClick, color = 'primary' }) => (
  <Button
    size="small"
    color={color}
    startIcon={icon}
    onClick={onClick}
    sx={{ textTransform: 'none', fontWeight: 600, py: 0, minWidth: 0 }}
  >
    {label}
  </Button>
);

export default TicketTypeSelector;
